import React, { useLayoutEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import Modal from '../../components/modal/Modal';
import { useAccessMode } from '../../context/AccessModeContext';
import { formatKst, INFO_ONLY_FLAGS, isCheckNeededRow, visibleFlags } from '../../features/sessionReport/labels';
import { SessionReportResult, SessionReportRow } from '../../features/sessionReport/types';
import { useSessionReport } from '../../features/sessionReport/hooks';
import { sessionReportKeys } from '../../features/sessionReport/keys';
import {
    createResolutionTasks, createRevokeTasks, hasResolveBasis, matchesResolutionBasis, refreshAfterResolution, RESOLUTION_CONFIRM_TEXT,
    ResolutionTask, resolutionDisplayKey, runResolutionTasks,
} from '../../features/sessionReport/resolution';

const resolutionMonth = (yearMonth: string) => `${Number(yearMonth.slice(5, 7))}월`;

export const useSessionReportResolution = ({ row, centerId, yearMonth, ghostDays, titleRef }: {
    row: SessionReportRow; centerId: string; yearMonth: string; ghostDays: number; titleRef?: React.RefObject<HTMLElement>;
}) => {
    const client = useQueryClient();
    const { isReadOnly } = useAccessMode();
    const [confirm, setConfirm] = useState<'resolve' | { yearMonth: string; resolutionIds: string[] } | null>(null);
    const [busy, setBusy] = useState(false);
    const running = useRef(false);
    const trigger = useRef<HTMLButtonElement | null>(null);
    const restoreFocus = useRef(false);
    const [tasks, setTasks] = useState<ResolutionTask[]>([]);
    const [notice, setNotice] = useState('');
    const [changedDuringProcessing, setChangedDuringProcessing] = useState(false);
    const [refreshFailed, setRefreshFailed] = useState(false);
    const params = { centerId, coachId: row.coachId, yearMonth, ghostDays };
    // 처리 기능이 있는 열린 강사 상세만 활성화해 무효화 시 한 번 조회해요.
    useSessionReport({ ...params, enabled: tasks.length > 0 || row.memberships.some(hasResolveBasis) });
    const displayKey = resolutionDisplayKey(params, row.rowKey);
    const detailKey = sessionReportKeys.report(centerId, yearMonth, ghostDays, row.coachId);
    useLayoutEffect(() => {
        if (confirm || !restoreFocus.current) return;
        const target = trigger.current?.isConnected && !trigger.current.disabled ? trigger.current : titleRef?.current;
        target?.focus({ preventScroll: true });
        if (!busy) restoreFocus.current = false;
    }, [confirm, busy, titleRef]);
    const closeConfirm = () => { restoreFocus.current = true; setConfirm(null); };
    const refresh = async (completed = tasks) => {
        try {
            await refreshAfterResolution(client, params);
            const result = client.getQueryData<SessionReportResult>(detailKey);
            const current = result?.status === 'ok' ? result.report.coaches.flatMap((coach) => Object.values(coach.sheets)
                .flatMap((sheet) => sheet.rows ?? [])).find((item) => item.rowKey === row.rowKey) : undefined;
            const changedAfterSave = !!current && completed.some((task) => task.done && !task.changed && task.body
                && current.memberships.some((m) => m.membershipId === task.body!.membershipId && m.resolution?.matches === false
                    && (!matchesResolutionBasis(m, task.body!) || m.resolution.id !== task.previousResolutionId)));
            // 저장 전에 시작한 조회가 같은 서명·과거 기록을 돌려주면 반영을 더 기다려요.
            if (current && completed.some((task) => task.done && !task.changed && current.memberships.some((m) => task.body
                ? m.membershipId === task.body.membershipId && matchesResolutionBasis(m, task.body) && m.resolution?.matches !== true
                    && (!m.resolution || m.resolution.id === task.previousResolutionId)
                : m.resolution?.id === task.resolutionId && m.resolution?.matches === true))) throw new Error('처리 기록 반영을 기다리고 있어요.');
            setChangedDuringProcessing(changedAfterSave);
            if (completed.length > 0 && completed.every((task) => task.done && !task.changed)) {
                setNotice(changedAfterSave ? '처리하는 사이 숫자가 바뀌었어요. 다시 확인해 주세요'
                    : completed[0].body ? '처리 기록을 저장했어요.' : '처리 완료를 되돌렸어요.');
            }
            setRefreshFailed(false);
            client.removeQueries({ queryKey: displayKey, exact: true });
        } catch {
            setRefreshFailed(true);
            // 저장은 됐지만 조회가 늦으면 다음 서버 응답까지 목록의 완료 표시를 유지해요.
            if (client.getQueryState(displayKey)) client.setQueryData(displayKey, {
                resolved: !!completed[0]?.body, awaitingData: client.getQueryData(detailKey),
            });
        }
    };
    const run = async (next: ResolutionTask[]) => {
        if (isReadOnly || running.current || !next.length) return;
        running.current = true;
        setBusy(true);
        closeConfirm();
        setNotice('');
        setChangedDuringProcessing(false);
        setTasks(next);
        client.setQueryData(displayKey, { resolved: !!next[0].body });
        try {
            const finished = await runResolutionTasks(client, centerId, next);
            if (!finished) return;
            setTasks([...next]);
            if (next.some((task) => !task.done || task.changed)) client.removeQueries({ queryKey: displayKey, exact: true });
            setNotice(next.some((task) => task.changed) ? '다른 곳에서 처리 기록이 바뀌었어요. 다시 확인해 주세요.'
                : next.some((task) => !task.done) ? '저장하지 못한 수강권만 다시 시도할 수 있어요.'
                : next[0].body ? '처리 기록을 저장했어요.' : '처리 완료를 되돌렸어요.');
            await refresh(next);
        } finally {
            running.current = false;
            setBusy(false);
        }
    };
    // 새로 받은 숫자·기록이 달라진 장은 과거 요청의 재시도 대상에서 뺀다.
    const retryTasks = tasks.filter((task) => !task.done && row.memberships.some((m) => task.body
        ? task.body.yearMonth === yearMonth && m.membershipId === task.body.membershipId && matchesResolutionBasis(m, task.body)
            && m.resolution !== undefined && m.resolution?.matches !== true && m.resolution?.id === task.previousResolutionId
        : m.resolution?.id === task.resolutionId && m.resolution?.matches === true));
    const matches = row.memberships.filter((m) => hasResolveBasis(m) && m.resolution?.matches === true && m.resolution.yearMonth <= yearMonth)
        .filter((m, index, members) => members.findIndex((item) => item.resolution!.id === m.resolution!.id) === index);
    // 표시할 달은 기존 기준을 따르고, 그 달의 일치하는 기록 전부를 안내와 되돌리기에 함께 써요.
    const resolutionGroups = Array.from(new Set(matches.map((m) => m.resolution!.yearMonth))).sort().reverse().map((month) => {
        const memberships = row.memberships.filter((m) => m.resolution?.matches === true && m.resolution.yearMonth === month)
            .filter((m, index, members) => members.findIndex((item) => item.resolution!.id === m.resolution!.id) === index);
        const latest = memberships.reduce((record, m) => Date.parse(m.resolution!.resolvedAt) > Date.parse(record.resolvedAt) ? m.resolution! : record,
            memberships[0].resolution!);
        const who = latest.resolvedBy.endsWith('관리자') ? latest.resolvedBy : `${latest.resolvedBy || '센터'} 관리자`;
        return { yearMonth: month, memberships, latest, who: `${who}${new Set(memberships.map((m) => m.resolution!.resolvedBy)).size > 1 ? ' 외' : ''}` };
    });
    const revokeMonth = (month: string, resolutionIds?: string[]) => createRevokeTasks(row.memberships.filter((m) =>
        m.resolution?.yearMonth === month && m.resolution.matches === true && (!resolutionIds || resolutionIds.includes(m.resolution.id))));
    const needsCheck = isCheckNeededRow({ ...row, resolved: false });
    const hasResolvableIssue = row.check?.status === 'MISMATCH'
        || visibleFlags(row.check?.flags).some((flag) => flag !== 'UNPAID_UNKNOWN' && !INFO_ONLY_FLAGS.has(flag));
    const recordUnavailable = needsCheck && row.memberships.some((m) => !!m.resolveBasis && m.resolution === undefined);
    const changed = row.memberships.flatMap((m) => hasResolveBasis(m) && m.resolution?.matches === false && m.resolution.yearMonth <= yearMonth ? [m.resolution] : [])
        .sort((a, b) => b.yearMonth.localeCompare(a.yearMonth) || b.resolvedAt.localeCompare(a.resolvedAt))[0];
    const canResolve = !isReadOnly && needsCheck && hasResolvableIssue && !recordUnavailable && row.resolved !== true
        && row.memberships.some((m) => hasResolveBasis(m) && m.resolution?.matches !== true);
    const problemMemberships = row.memberships.filter(hasResolveBasis);
    const totalCount = new Set(problemMemberships.map((m) => m.membershipId)).size;
    const remainingCount = new Set(problemMemberships.filter((m) => m.resolution?.matches !== true).map((m) => m.membershipId)).size;
    const resolvedCount = totalCount - remainingCount;
    // 일부 장을 되돌려도 남은 기록은 달별로 되돌릴 수 있어요. 행 전체 완료는 resolved만 따라요.
    const partiallyResolved = row.resolved !== true && matches.length > 0
        && remainingCount > 0;
    const banner = <>
        {needsCheck && (row.resolved === true || partiallyResolved) && <div role="status" className="mb-[16px] space-y-[8px] rounded-[12px] bg-[#F0FDF4] px-[16px] py-[12px] text-[14px] leading-[22px] text-[#15803D]">
            {partiallyResolved && <p>수강권 {totalCount}개 중 {resolvedCount}개 처리 완료 — 남은 {remainingCount}개를 확인해 주세요</p>}
            {matches.length === 0 && <span>처리 완료</span>}
            {resolutionGroups.map((group) => {
                const inherited = group.yearMonth < yearMonth;
                const month = resolutionMonth(group.yearMonth);
                return <div key={group.yearMonth}>
                    <div className="flex flex-wrap items-center justify-between gap-x-[12px] gap-y-[4px]">
                        <span>{inherited ? `${month}에 처리 완료 · ${group.who} · 이 달은 숫자 변화가 없어 그대로 두었어요`
                            : `처리 완료 · ${group.who} · ${formatKst(group.latest.resolvedAt, 'M월 D일')}${group.memberships.length > 1 ? ` · 수강권 ${group.memberships.length}개` : ''}`}</span>
                        {!isReadOnly && <button type="button" disabled={busy} aria-label={inherited ? `되돌리기(${month} 처리)` : undefined} onClick={(event) => {
                            trigger.current = event.currentTarget;
                            if (inherited) setConfirm({ yearMonth: group.yearMonth, resolutionIds: group.memberships.map((m) => m.resolution!.id) });
                            else void run(revokeMonth(group.yearMonth));
                        }} className="min-h-[40px] shrink-0 underline underline-offset-4 disabled:opacity-50">되돌리기</button>}
                    </div>
                    <details className="mt-[4px]">
                        <summary className="min-h-[40px] w-fit cursor-pointer list-none py-[4px] [&::-webkit-details-marker]:hidden">어떤 수강권인지 보기 ›</summary>
                        <ul className="mt-[4px] space-y-[4px] break-words">
                            {group.memberships.map((m) => <li key={m.resolution!.id}>{m.name}</li>)}
                        </ul>
                    </details>
                </div>;
            })}
        </div>}
        {needsCheck && row.resolved !== true && changed && !busy && !refreshFailed && !changedDuringProcessing && <p role="status" className="mb-[12px] rounded-[10px] bg-[#FFFBEB] px-[16px] py-[12px] text-[14px] leading-[22px] text-[#92400E]">
            {resolutionMonth(changed.yearMonth)} 처리 뒤 숫자가 바뀌었어요. 숫자가 바뀌어서 다시 보여 드려요
        </p>}
    </>;
    const actions = <>
        {recordUnavailable && <p className="text-[14px] leading-[22px] text-[#4E5968]">처리 기록을 잠시 불러오지 못했어요</p>}
        {canResolve && !refreshFailed && retryTasks.length === 0 && <button type="button" ref={trigger} disabled={busy} onClick={() => setConfirm('resolve')}
            className="min-h-[42px] rounded-[10px] border border-solid border-[#DCE1E7] bg-white px-[16px] text-[14px] font-medium text-[#4E5968] hover:bg-[#F8F9FB] disabled:opacity-50">
            {partiallyResolved ? '남은 수강권도 처리 완료' : '문제없어요 · 처리 완료'}
        </button>}
        {(busy || notice || refreshFailed) && <div className="w-full text-[14px] leading-[22px] text-[#4E5968]">
            <p role="status">{busy ? '처리하고 있어요.' : notice}</p>
            {!isReadOnly && !recordUnavailable && !busy && retryTasks.length > 0 && <ul className="mt-[8px] space-y-[8px]">
                {retryTasks.map((task) => <li key={task.key}>
                    <span className="text-[#B91C1C]">{task.name} · {task.error}</span>
                </li>)}
                <li><button type="button" onClick={() => void run(tasks.filter((item) => item.done || retryTasks.includes(item)))}
                    className="min-h-[40px] text-[#3D73DD] underline">{retryTasks.length > 1 ? '실패한 수강권 다시 시도' : '다시 시도'}</button></li>
            </ul>}
            {!busy && refreshFailed && <p className="mt-[8px] text-[#B45309]">{tasks.length > 0 && tasks.every((task) => task.done && !task.changed)
                ? tasks[0].body ? '처리 완료로 저장했어요. 숫자 반영이 늦어지고 있어요 — 잠시 뒤 새로고침해 주세요'
                    : '처리 완료를 되돌렸어요. 숫자 반영이 늦어지고 있어요 — 잠시 뒤 새로고침해 주세요'
                : '처리 뒤 자료를 불러오지 못했어요. 잠시 뒤 다시 조회해 주세요'}
                <button type="button" onClick={async () => { if (running.current) return; running.current = true; setBusy(true); try { await refresh(); } finally { running.current = false; setBusy(false); } }}
                    className="ml-[8px] min-h-[40px] underline">다시 조회</button>
            </p>}
        </div>}
        {confirm && <Modal isCloseButton isCloseOutside onClose={closeConfirm}
            whiteViewWidth="min(440px, calc(100vw - 32px))" borderRadius={20} modalAlign="center">
            <div role="alertdialog" aria-modal="true" aria-labelledby="resolution-title" aria-describedby="resolution-description" className="p-[28px]">
                <h2 id="resolution-title" className="mb-[12px] text-[20px] font-semibold text-[#202630]">{confirm === 'resolve' ? '처리 완료로 둘까요?' : '처리 완료를 되돌릴까요?'}</h2>
                <div id="resolution-description" className="break-keep text-[15px] leading-[25px] text-[#4E5968]">
                    <p>{confirm === 'resolve' ? RESOLUTION_CONFIRM_TEXT
                        : `${resolutionMonth(confirm.yearMonth)}에 한 처리 완료를 되돌려요. ${resolutionMonth(confirm.yearMonth)}과 그 뒤 달에서 다시 손볼 목록에 보여요.`}</p>
                    {confirm === 'resolve' && <p className="mt-[8px]">언제든 되돌릴 수 있어요.</p>}
                    {confirm === 'resolve' && (row.unpaidAmount === null || row.check?.flags.includes('UNPAID_UNKNOWN')) && <p className="mt-[8px]">미수 계산은 따로 확인이 필요해서 그 안내는 남아 있어요.</p>}
                </div>
                <div className="mt-[24px] flex justify-end gap-[8px]">
                    <button type="button" onClick={() => {
                        // 확인 창을 연 뒤 새로 생긴 다른 처리 기록은 되돌리지 않아요.
                        const next = confirm === 'resolve' ? createResolutionTasks(client, centerId, yearMonth, row.memberships)
                            : revokeMonth(confirm.yearMonth, confirm.resolutionIds);
                        if (next.length) void run(next);
                        else closeConfirm();
                    }}
                        className="min-h-[44px] rounded-[10px] bg-[#3D73DD] px-[20px] text-[15px] font-medium text-white">{confirm === 'resolve' ? '처리 완료' : '되돌리기'}</button>
                    <button type="button" autoFocus onClick={closeConfirm}
                        className="min-h-[44px] rounded-[10px] bg-[#F2F4F6] px-[20px] text-[15px] text-[#4E5968]">취소</button>
                </div>
            </div>
        </Modal>}
    </>;
    return { banner, actions, busy, canResolve, isBusy: () => running.current };
};
